import React, { useMemo } from 'react';
import { T, Var, useGT } from 'gt-react';
import { FixedSizeList as List } from 'react-window';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import { WarningCircleIcon } from '@phosphor-icons/react';
import MDTypography from 'src/components/md/MDTypography';
import useProductVariant from 'src/api/inventory-management/products/useProductVariant';

const ITEM_HEIGHT = 75;

interface VariantItemData {
  variants: Array<{
    id: string;
    originMetadata?: unknown;
    sku?: string | null;
  }>;
  selectedVariantId?: string;
  onSelect: (variantId: string) => void;
}

const VariantListItem = ({
  index,
  style,
  data,
}: {
  index: number;
  style: React.CSSProperties;
  data: VariantItemData;
}) => {
  const gt = useGT();
  const { variants, selectedVariantId, onSelect } = data;
  const variant = variants[index];

  const { data: variantData, isLoading, error } = useProductVariant(variant.id);
  const productVariant = variantData?.productVariant;
  const attributeDescriptions = variantData?.attributeDescriptions || [];
  const hasAttributes = attributeDescriptions.length > 0;

  const isSelected = selectedVariantId === variant.id;

  return (
    <div style={style}>
      <button
        type="button"
        role="radio"
        aria-checked={isSelected}
        className={`flex w-full items-start border-b px-0 py-2 text-left transition-colors${isSelected ? ' bg-teal-500/[0.12] hover:bg-teal-500/[0.12]' : ' bg-transparent hover:bg-black/[0.04]'}`}
        style={{ height: ITEM_HEIGHT, minHeight: ITEM_HEIGHT }}
        onClick={() => onSelect(variant.id)}
      >
        {/* Visual radio indicator — a Radix RadioGroupItem can't be used here:
            these virtualized rows aren't wrapped in a RadioGroup context, and
            the enclosing button already carries the radio role/selection. */}
        <span
          aria-hidden="true"
          className={`ml-2 mr-1 mt-1 flex h-4 w-4 shrink-0 self-start items-center justify-center rounded-full border ${isSelected ? 'border-primary' : 'border-input'}`}
        >
          {isSelected && <span className="h-2 w-2 rounded-full bg-primary" />}
        </span>
        {isLoading ? (
          <div className="flex items-center justify-center flex-1 ml-4">
            <Spinner size="sm" />
            <MDTypography variant="body2" ml={2}>
              <T context="short loading indicator in variant list row">
                Loading...
              </T>
            </MDTypography>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center flex-1 ml-4">
            <WarningCircleIcon size={20} className="mr-1" />
            <MDTypography variant="body2" color="error.main">
              {gt('Error loading variant', {
                context: 'error when variant row failed to load',
              })}
            </MDTypography>
          </div>
        ) : productVariant ? (
          <div className="flex flex-col flex-1">
            <MDTypography
              variant="body2"
              fontWeight={isSelected ? 'medium' : 'regular'}
              color={isSelected ? '#00a4a4' : 'text.primary'}
            >
              {productVariant.originMetadata?.NAMEINVC ||
                productVariant.sku ||
                gt('Unknown', {
                  context: 'fallback when product variant has no name',
                })}
              {productVariant.originMetadata?.PRODUCT_ID && (
                <T context="optional product id suffix in parentheses">
                  {' '}
                  (<Var>{String(productVariant.originMetadata.PRODUCT_ID)}</Var>
                  )
                </T>
              )}
            </MDTypography>
            {hasAttributes && (
              <div className="flex flex-wrap gap-1 mt-1 max-w-full">
                {attributeDescriptions.map((attr, index) => (
                  <Badge
                    key={index}
                    variant="outline"
                    className={
                      isSelected
                        ? 'border-teal-500/50 text-teal-700 bg-teal-500/[0.08] text-[0.65rem]'
                        : 'border-black/[0.23] text-muted-foreground text-[0.65rem]'
                    }
                  >
                    <span className="font-medium opacity-90">
                      <T context="product attribute name before value">
                        <Var>{attr.name}</Var>:
                      </T>
                    </span>{' '}
                    <span className="opacity-80">{attr.value}</span>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        ) : (
          <span className="text-sm ml-1">
            {gt('Variant not found', {
              context: 'product variant missing in list row',
            })}
          </span>
        )}
      </button>
    </div>
  );
};

interface Props {
  variants: Array<{
    id: string;
    originMetadata?: unknown;
    sku?: string | null;
  }>;
  selectedVariantId?: string;
  onSelect: (variantId: string) => void;
  maxHeight?: number;
}

const VirtualizedVariantList: React.FC<Props> = ({
  variants,
  selectedVariantId,
  onSelect,
  maxHeight = 400,
}) => {
  const gt = useGT();

  const itemData = useMemo(
    () => ({
      variants,
      selectedVariantId,
      onSelect,
    }),
    [variants, selectedVariantId, onSelect],
  );

  if (variants.length === 0) {
    return (
      <div className="p-4 text-center">
        <MDTypography variant="body2" color="text.secondary">
          {gt('No variants found', {
            context: 'empty state when no product variants match',
          })}
        </MDTypography>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg shadow-[0_2px_12px_rgba(0,0,0,0.08)] overflow-hidden bg-card">
      <List
        height={Math.min(maxHeight, variants.length * ITEM_HEIGHT)}
        width="100%"
        itemCount={variants.length}
        itemSize={ITEM_HEIGHT}
        itemData={itemData}
        overscanCount={5} // Render a few extra items for smooth scrolling
      >
        {VariantListItem}
      </List>
    </div>
  );
};

export default VirtualizedVariantList;
